import {
  startOfWeek,
  endOfWeek,
  nextSaturday,
  nextSunday,
  isSaturday,
  isSunday,
  addDays,
  format,
  parseISO,
  isValid,
  isSameDay
} from 'date-fns'

/**
 * Gets the date range for "This Weekend"
 * Returns Saturday-Sunday of the current or upcoming weekend
 */
export function getThisWeekend(): { start: Date; end: Date } {
  const today = new Date()
  
  // If today is Saturday or Sunday, use this weekend
  if (isSaturday(today)) {
    return {
      start: today,
      end: nextSunday(today)
    }
  }
  
  if (isSunday(today)) {
    return {
      start: today,
      end: today
    }
  }
  
  // Otherwise, get the next weekend
  const saturday = nextSaturday(today)
  const sunday = nextSunday(saturday)
  
  return {
    start: saturday,
    end: sunday
  }
}

/**
 * Gets the date range for "Next Weekend"
 * Returns Saturday-Sunday for the weekend after the upcoming one
 */
export function getNextWeekend(): { start: Date; end: Date } {
  const thisWeekend = getThisWeekend()

  return {
    start: addDays(thisWeekend.start, 7),
    end: addDays(thisWeekend.end, 7)
  }
}

/**
 * Gets the date range for "Next Week"
 * Returns Monday-Sunday of next week
 */
export function getNextWeek(): { start: Date; end: Date } {
  const today = new Date()
  // Get next Monday (if today is Monday, get next Monday)
  const nextMonday = startOfWeek(addDays(today, 7), { weekStartsOn: 1 })
  const nextSunday = endOfWeek(nextMonday, { weekStartsOn: 1 })
  
  return {
    start: nextMonday,
    end: nextSunday
  }
}

/**
 * Gets a date range starting from today for a specified number of days
 */
export function getWeekFromToday(days: number): { start: Date; end: Date } {
  const today = new Date()
  return {
    start: today,
    end: addDays(today, days)
  }
}

/**
 * Parses a date-only string (YYYY-MM-DD) as a local date
 * This prevents timezone issues where dates are interpreted as UTC
 */
export function parseLocalDate(dateString: string): Date {
  if (!dateString) {
    throw new Error('Date string is required')
  }
  
  // Parse YYYY-MM-DD format as local date
  const [year, month, day] = dateString.split('-').map(Number)
  if (!year || !month || !day) {
    throw new Error('Invalid date format. Expected YYYY-MM-DD')
  }
  
  // Create date in local timezone (month is 0-indexed)
  return new Date(year, month - 1, day)
}

/**
 * Formats a date string (YYYY-MM-DD) to a readable format
 * Uses local date parsing to avoid timezone issues
 */
export function formatDateForDisplay(dateString: string): string {
  if (!dateString) return ''
  
  try {
    const date = parseLocalDate(dateString)
    if (!isValid(date)) return dateString
    return format(date, 'MMM d, yyyy')
  } catch {
    return dateString
  }
}

/**
 * Formats a Date object to YYYY-MM-DD format for API
 */
export function formatDateForAPI(date: Date): string {
  return format(date, 'yyyy-MM-dd')
}

/**
 * Formats a trip date range for compact display (e.g. sidebar thumbnails).
 * Same year: "Mar 12 – 18"; cross-year: "Dec 28, 2024 – Jan 4, 2025".
 * Returns empty string if dates are missing or invalid.
 */
export function formatTripDateRange(
  startDate: string | null,
  endDate: string | null
): string {
  if (!startDate || !endDate) return ''
  try {
    const start = parseLocalDate(startDate)
    const end = parseLocalDate(endDate)
    if (!isValid(start) || !isValid(end)) return ''
    const sameYear = start.getFullYear() === end.getFullYear()
    if (sameYear) {
      return `${format(start, 'MMM d')} – ${format(end, 'MMM d')}`
    }
    return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`
  } catch {
    return ''
  }
}

/**
 * Formats a trip date range with weekday context for compact metadata pills.
 * Examples:
 * - Same day: "Sat, Apr 4"
 * - Same month: "Sat-Sun, Apr 4-5"
 * - Cross month/year: "Sat, Dec 31 - Mon, Jan 2"
 */
export function formatTripDateRangeWithWeekdays(
  startDate: string | null,
  endDate: string | null
): string {
  if (!startDate || !endDate) return ''

  try {
    const start = parseLocalDate(startDate)
    const end = parseLocalDate(endDate)
    if (!isValid(start) || !isValid(end)) return ''

    if (isSameDay(start, end)) {
      return format(start, 'EEE, MMM d')
    }

    const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()
    if (sameMonth) {
      return `${format(start, 'EEE')}-${format(end, 'EEE')}, ${format(start, 'MMM d')}-${format(end, 'd')}`
    }

    return `${format(start, 'EEE, MMM d')} - ${format(end, 'EEE, MMM d')}`
  } catch {
    return ''
  }
}

/**
 * Gets the number of days for a trip based on start and end dates.
 * Returns null if either date is missing. Includes both start and end days.
 */
export function getTripDurationDays(trip: {
  start_date: string | null
  end_date: string | null
}): number | null {
  if (!trip.start_date || !trip.end_date) return null
  try {
    const start = parseLocalDate(trip.start_date)
    const end = parseLocalDate(trip.end_date)
    if (!isValid(start) || !isValid(end)) return null
    const diffTime = end.getTime() - start.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
    return diffDays > 0 ? diffDays : null
  } catch {
    return null
  }
}

/**
 * Validates that end date is after start date
 */
export function validateDateRange(
  startDate: string,
  endDate: string
): { isValid: boolean; error?: string } {
  if (!startDate || !endDate) {
    return { isValid: false, error: 'Both dates are required' }
  }
  
  try {
    const start = parseISO(startDate)
    const end = parseISO(endDate)
    
    if (!isValid(start) || !isValid(end)) {
      return { isValid: false, error: 'Invalid date format' }
    }
    
    if (end < start) {
      return { isValid: false, error: 'End date must be after start date' }
    }
    
    return { isValid: true }
  } catch {
    return { isValid: false, error: 'Invalid date format' }
  }
}

